import { useMemo } from 'react';
import { useLoaderData } from 'react-router';

import { useEnquiryModal } from '~/modules/enquiry/hooks/use-enquiry-modal';
import type { CreateEnquirySource } from '~/modules/enquiry/types';
import type { loader } from '~/routes/_app.product.$urlSegment';

/**
 * The standard product enquiry modal, pre-configured from the product loader
 * (professional, branch options, Product CTA label, product title + image).
 * Shared by the gallery photo viewer, the downloads row and the spec
 * accordion so the config lives in one place.
 *
 * `source` names the surface the enquiry started from. It is one of only two
 * keys that survive to the warehouse, and every call site here used to leave it
 * unset — so every enquiry on the page arrived as the generic `EnquiryModal`
 * and the product page's CTAs could not be told apart.
 */
export const useProductEnquiryModal = (
    source: Exclude<CreateEnquirySource, 'InPage'> = 'ProductDetailsCTA',
) => {
    const { product, branchOptions, primaryCTA } =
        useLoaderData<typeof loader>();
    const professional = product.Professional;

    // Memoised so the returned `openEnquiryModal` keeps a stable identity across
    // renders — an unstable config would churn any island mode that closes over
    // it (re-registering every section each render).
    const data = useMemo(
        () => ({
            professional: {
                ID: professional.ID,
                Title: professional.Title,
                branchOptions,
                CompanyLogo: professional.CompanyLogo ?? '',
                LogoBackgroundColor:
                    professional.LogoBackgroundColor ?? 'white',
                CustomEnquiryMessage:
                    professional.CustomEnquiryMessage ?? undefined,
            },
            itemType: 'product' as const,
            siteTreeID: product.ID,
            source,
            ctaLabel: primaryCTA.primaryCTALabelTitleCase,
            includeBranch: branchOptions.length > 0,
            itemTitle: product.Title,
            itemImage:
                product.LibraryImages?.[0]?.LibraryImageFile?.PhotoTile ??
                undefined,
            // The gate renders this across a 478px pane, so it wants the
            // detail-page rendition the revamped page itself uses — PhotoTile
            // is sized for a grid tile and goes soft at that width.
            brandImage:
                product.LibraryImages?.[0]?.LibraryImageFile?.DetailPageSquare
                    ?.Desktop ?? undefined,
        }),
        [product, professional, branchOptions, primaryCTA, source],
    );

    return useEnquiryModal(data);
};
